import { z } from 'zod';

// Financial/monetary amount validation
export const amountSchema = z.number()
  .min(0.01, 'Amount must be at least $0.01')
  .max(10000, 'Amount cannot exceed $10,000')
  .refine((val) => !isNaN(val) && isFinite(val), 'Invalid amount');

export const depositAmountSchema = z.number()
  .min(1, 'Minimum deposit is $1')
  .max(1000, 'Maximum deposit is $1,000')
  .refine((val) => !isNaN(val) && isFinite(val), 'Invalid amount');

export const withdrawalAmountSchema = (maxBalance: number, minWithdrawal = 10) => z.number()
  .min(minWithdrawal, `Minimum withdrawal is $${minWithdrawal}`)
  .max(maxBalance, 'Amount exceeds available balance')
  .refine((val) => !isNaN(val) && isFinite(val), 'Invalid amount');

// League configuration validation
export const teamCountSchema = z.number()
  .int('Team count must be a whole number')
  .min(2, 'Minimum 2 teams required')
  .max(32, 'Maximum 32 teams allowed');

export const roundsSchema = z.number()
  .int('Rounds must be a whole number')
  .min(1, 'At least 1 round required')
  .max(30, 'Maximum 30 rounds allowed');

export const faabBudgetSchema = z.number()
  .int('Budget must be a whole number')
  .min(0, 'Budget cannot be negative')
  .max(10000, 'Maximum budget is $10,000');

export const auctionBudgetSchema = z.number()
  .int('Budget must be a whole number')
  .min(50, 'Minimum budget is $50')
  .max(1000, 'Maximum budget is $1,000');

export const minBidSchema = z.number()
  .int('Minimum bid must be a whole number')
  .min(1, 'Minimum bid is $1')
  .max(100, 'Maximum minimum bid is $100');

export const sabotageSpinCostSchema = z.number()
  .int('Cost must be a whole number')
  .min(0, 'Cost cannot be negative')
  .max(100, 'Maximum cost is $100');

export const bonusAmountSchema = z.number()
  .min(0, 'Amount cannot be negative')
  .max(10000, 'Maximum amount is $10,000');

export const regularSeasonWeeksSchema = z.number()
  .int('Weeks must be a whole number')
  .min(1, 'At least 1 week required')
  .max(26, 'Maximum 26 weeks');

// Banking/card schemas removed — payment processors handle PAN/CVV/ACH server-side.
// Do NOT add raw card or bank-account validators back here.

// ── Password policy ──
// 10+ chars, with at least one uppercase, one lowercase, one digit.
// Server-side rate-limiting + breach-list checks should be added at the
// Supabase Auth layer; this is the client-side gate.
export const PASSWORD_REQUIREMENTS_TEXT =
  'Password must be at least 10 characters with uppercase, lowercase, and a number';

export const isPasswordStrong = (password: string): boolean => {
  if (password.length < 10) return false;
  if (!/[A-Z]/.test(password)) return false;
  if (!/[a-z]/.test(password)) return false;
  if (!/[0-9]/.test(password)) return false;
  return true;
};

export const emailSchema = z.string()
  .email('Invalid email address')
  .max(255, 'Email too long');

export const usernameSchema = z.string()
  .min(3, 'Username must be at least 3 characters')
  .max(50, 'Username too long');

// Helper functions
export const parseNumericInput = (value: string, defaultValue = 0): number => {
  const parsed = parseFloat(value);
  if (isNaN(parsed) || !isFinite(parsed)) {
    return defaultValue;
  }
  return parsed;
};

export const parseIntegerInput = (value: string, defaultValue = 0): number => {
  const parsed = parseInt(value, 10);
  if (isNaN(parsed) || !isFinite(parsed)) {
    return defaultValue;
  }
  return parsed;
};

export const validateNumericRange = (
  value: number,
  min: number,
  max: number
): { isValid: boolean; error?: string } => {
  if (isNaN(value) || !isFinite(value)) {
    return { isValid: false, error: 'Invalid number' };
  }
  if (value < min) {
    return { isValid: false, error: `Value must be at least ${min}` };
  }
  if (value > max) {
    return { isValid: false, error: `Value cannot exceed ${max}` };
  }
  return { isValid: true };
};
